/**
 * Vendor
 */
import { useRef } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import LazyLoad from "react-lazyload";

/** ========== */
/* Components
 */
import { Tab } from "@headlessui/react";
import { Autoplay, EffectFade, Mousewheel, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

/**
 * Vendor
 */
import { AutoConvertPriceWithUnit } from "common/priceConvert";

import ButtonNew from "components/atoms/ButtonNew/index";
import CheckContent from "components/atoms/CheckContent";
import Img from "components/atoms/Img";
import TawkToButton from "components/atoms/TawkToButton/index";
import Title from "components/atoms/Title";
import FormCountries from "components/molecules/FormCountries";
import FormNewsLetter from "components/molecules/FormNewsletter";
import RefundGuarantee from "components/molecules/RefundGuarantee";
import TabListNew from "components/molecules/TabListNew/index";
import Banner from "components/organisms/Banner";
import EbookDownload from "components/organisms/EbookDownload/index";
import Faqs from "components/organisms/Faqs";
import HowItWorks from "components/organisms/HowItWorks";
import NewsSlider from "components/organisms/NewsSlider";
import OffshoreCompanyRegistration from "components/organisms/OffshoreCompanyRegistration";
import RequiredDocument from "components/organisms/RequiredDocument";
import StepSlider from "components/organisms/StepSlider";
import WhatMakeUsDifferent from "components/organisms/WhatMakeUsDifferent";
import styles from "components/templates/sg/sg-foreigner/Index.module.scss";

import CompanyNameCheck from "../company-incorporation/CompanyNameCheck";
import SGPackage from "../sg-foreigner/SGPackage/index";

const CompanyIncorporationLocal = ({ data, dataJson, ...props }) => {
  const swiperRef = useRef();

  return (
    <>
      <Banner
        minHeightBanner="banner-min-h-635"
        breadcrumbs={dataJson.breadcrumbs}
        title={"Company Incorporation <br/>In Singapore"}
        titleProps={{
          className: "banner-default-title mb-6 lg:mb-10 capitalize",
          textColor: "banner-title-default-color",
        }}
        leftClassName="lg:col-7"
        rightClassName="lg:col-5"
        rightContent={
          <Img
            src={
              "/assets/images/sg/company-incorporation-local/banner-sg-incorp_local.png"
            }
            wrapper={{
              className: "bbc-banner-image-country pb-10 lg:pb-0 ",
            }}
            className="w-full"
            width={560}
            height={456}
            priority={true}
            alt="Singapore company incorporation service"
          />
        }
        bgColor={"bg-bbc-country"}
        dataTrustpilot={{
          dataStatic: dataJson.dataTrustpilot,
          dataDefault: data.dataTrustpilot,
        }}
        leftContent={
          <div className="mx-auto mb-6 flex w-fit flex-col items-center lg:mb-12 lg:w-full lg:flex-row lg:pb-1.5 ">
            <TawkToButton othersClass="lg:mr-6 mb-3 lg:mb-0 w-fit lg:whitespace-nowrap border border-bbc-brand-500 capitalize">
              Talk to our expert
            </TawkToButton>
            <ButtonNew
              variant={"outline"}
              useRouter
              as="a"
              href="/contact-us"
              othersClass={"w-full text-center lg:w-fit capitalize"}
            >
              Contact us
            </ButtonNew>
          </div>
        }
      />
      <section className="lg:mb-0">
        <div className="container">
          <div id="package" className="pt-10 lg:pt-20">
            <SGPackage data={data} {...dataJson.dataPackage} reverse />
          </div>

          <RefundGuarantee className="pt-10" />
        </div>
      </section>
      <Img
        src="/assets/images/wave/opposition-wave.png"
        width={1920}
        height={86}
        className="hidden w-full lg:block"
        wrapper={{ className: "w-full" }}
        alt="Wave opposition"
      />

      <div className="lg:bg-bbc-brand-100">
        <section className=" lg:pt-20">
          <div className="container">
            <div className=" text-center">
              <Title
                text={"Additional services"}
                className={"bbc-title mb-4 text-center lg:mb-2"}
              />
              <p>* Extra courier fees apply </p>
            </div>
            <div className="mx-auto mt-6 grid gap-6 lg:mt-10 lg:w-4/5 lg:grid-cols-2 lg:gap-10">
              {dataJson.dataAdditionalServices.map((item) => (
                <div
                  key={item.service}
                  className={
                    " shadow-bbc  justify-between rounded-3xl bg-white py-8 text-center lg:py-11"
                  }
                >
                  <p
                    className={
                      " border-b px-4 pb-10 text-2xl font-semibold leading-9  text-bbc-brand-700  lg:px-8 lg:pb-8"
                    }
                  >
                    {item.service}
                  </p>
                  <div className={`${styles.fee} pt-10 lg:pt-8`}>
                    {AutoConvertPriceWithUnit(item.fee, item.unit || 1)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        <section className="mb-0 ">
          <OffshoreCompanyRegistration data={dataJson.dataRegistration} />
        </section>
        <section className="mb-0 pt-10 lg:pt-20">
          {dataJson.dataBusinessGrant && (
            <div className="container">
              <Title
                text={dataJson.dataBusinessGrant.title}
                className={"bbc-title  mb-4 text-center lg:mb-6"}
              />

              <div
                className="mb-6 space-y-6 lg:mb-10 lg:px-8"
                dangerouslySetInnerHTML={{
                  __html: dataJson.dataBusinessGrant.description,
                }}
              ></div>
              <div className="relative hidden px-32 pt-[100px] pb-[60px] text-bbc-white lg:block">
                <div
                  style={{ zIndex: "1" }}
                  className="absolute left-2 top-0 h-full w-full rounded-[40px] bg-[#00A6FF] after:absolute after:top-2 after:-right-2 after:z-0 after:h-[95%] after:w-[105%] after:-rotate-2 after:rounded-[40px] after:bg-bbc-brand-700 after:content-['']"
                ></div>
                <Swiper
                  loop={true}
                  autoplay={true}
                  slidesPerView={1}
                  spaceBetween={30}
                  mousewheel={true}
                  allowTouchMove={true}
                  effect={"fade"}
                  fadeEffect={{
                    crossFade: true,
                  }}
                  onBeforeInit={(swiper) => {
                    swiperRef.current = swiper;
                  }}
                  pagination={{
                    el: ".bbc-slide-pagination",
                    clickable: true,
                    bulletClass: "bbc-pagination-bullet !border-bbc-white",
                    bulletActiveClass:
                      "bbc-pagination-bullet-active h-2.5 w-10 !bg-bbc-white",
                  }}
                  breakpoints={{
                    320: {
                      allowTouchMove: true,
                    },
                    1024: {
                      allowTouchMove: true,
                    },
                  }}
                  modules={[Mousewheel, EffectFade, Pagination, Autoplay]}
                  className="mySwiper"
                  {...props}
                >
                  {dataJson.dataBusinessGrant.listGrant?.length > 0 &&
                    dataJson.dataBusinessGrant.listGrant?.map((item) => (
                      <SwiperSlide key={item.title}>
                        <div className="row">
                          <div className="grid place-items-center lg:col-6">
                            <Img src={item.imgSrc} alt={item.imgAlt} />
                          </div>
                          <div className="col-6">
                            <Title
                              as="h4"
                              className="text-2xl font-semibold text-bbc-yellow-500"
                            >
                              {item.title}
                            </Title>
                            {item?.content.length > 0 &&
                              item?.content.map((el) => (
                                <div key={el.subTitle} className="mt-4">
                                  <p className="mb-1 text-lg text-bbc-yellow-500">
                                    {el.subTitle}
                                  </p>
                                  {el?.listContent.length > 0 &&
                                    el?.listContent.map((a, b) => (
                                      <div
                                        key={b}
                                        dangerouslySetInnerHTML={{
                                          __html: a.text,
                                        }}
                                      />
                                    ))}
                                </div>
                              ))}
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                  <div
                    className={"bbc-slide-pagination z-20 pt-6 text-center"}
                  ></div>
                </Swiper>
                <div
                  style={{ backgroundColor: "rgba(191, 189, 189, 0.2)" }}
                  onClick={() => swiperRef.current?.slidePrev()}
                  className={
                    "bbc-button-prev absolute top-1/2 left-10 z-10 hidden h-12 w-12 -translate-y-1/2 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full transition-all duration-150 ease-in-out hover:opacity-60 lg:flex"
                  }
                >
                  <FaChevronLeft size={16} className="text-white" />
                </div>
                <div
                  style={{ backgroundColor: "rgba(191, 189, 189, 0.2)" }}
                  onClick={() => swiperRef.current?.slideNext()}
                  className={
                    "bbc-button-next absolute top-1/2 right-10 z-10 hidden h-12 w-12 -translate-y-1/2 translate-x-1/2 cursor-pointer items-center justify-center rounded-full transition-all duration-150 ease-in-out hover:opacity-60 lg:flex"
                  }
                >
                  <FaChevronRight size={16} className="text-white" />
                </div>
              </div>
              <div className="block lg:hidden">
                <TabListNew
                  data={dataJson.dataBusinessGrant.listGrant?.map(
                    (item) => item.title
                  )}
                  activeTab={dataJson.dataBusinessGrant.listGrant?.activeTab}
                  tabListClass={{
                    display: "flex justify-center",
                    length: "w-full",
                    padding: "px-1 py-3",
                    border: "border-0",
                    othersClass: "whitespace-nowrap overflow-x-auto mb-6",
                  }}
                  tabClass={{
                    padding: "py-2 px-4 ",
                    tabColor:
                      "text-bbc-neutral-600 border-bbc-neutral-600 hover:text-bbc-brand-500 hover:border-bbc-brand-500",
                    othersClass: "mr-2 last:mr-0 font-medium",
                  }}
                >
                  <Tab.Panels>
                    {dataJson.dataBusinessGrant.listGrant?.map(
                      (item, index) => {
                        return (
                          <Tab.Panel
                            className="focus:outline-none focus:outline-0 focus:ring-0"
                            as="div"
                            key={`tabs-${index}`}
                          >
                            <div className="mt-6">
                              {item.content?.map((el, idx) => {
                                return (
                                  <div
                                    key={`item-content-${idx}`}
                                    className="mb-4 rounded-2xl bg-bbc-white px-4 pb-4 shadow-[0_16px_40px_0_rgba(181,181,181,0.25)]"
                                  >
                                    <h5 className="border-b border-bbc-neutral-500 px-10 py-4 text-center font-semibold text-bbc-brand-700">
                                      {el.subTitle}
                                    </h5>
                                    {el.listContent?.map((a) => (
                                      <div
                                        key={a.text}
                                        className="mt-6"
                                        dangerouslySetInnerHTML={{
                                          __html: a.text,
                                        }}
                                      />
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          </Tab.Panel>
                        );
                      }
                    )}
                  </Tab.Panels>
                </TabListNew>
              </div>
              <div className="mt-6 flex flex-col items-center lg:mt-7">
                <p>{dataJson.dataBusinessGrant?.note}</p>
                <div className="mt-6">
                  <TawkToButton>Chat With Us</TawkToButton>
                </div>
              </div>
            </div>
          )}
        </section>
        <section className="mb-0 pt-10 lg:pt-20">
          <div className="container">
            <div className="row">
              <Title
                text="Accounting & Tax Filing Service In Singapore"
                className="bbc-title mb-6 block text-center text-bbc-30px text-bbc-brand-700 lg:hidden"
                as="h2"
              ></Title>
              <div className="col-12 order-last flex flex-col justify-center lg:col-6 lg:order-first">
                <Title
                  text="Accounting & Tax Filing Service In Singapore"
                  className="bbc-title mb-6 hidden text-bbc-30px text-bbc-brand-700 lg:block"
                  as="h2"
                ></Title>
                <div className="mb-6 mt-6 lg:mt-0">
                  <p>
                    Companies in Singapore are required to comply with annual
                    accounting and tax filing requirements in accordance with
                    the Accounting and Corporate Regulatory Authority (ACRA) and
                    the Inland Revenue Authority of Singapore (IRAS).
                  </p>
                  <p className="mt-6">
                    Our qualified accounting service helps you handle
                    complexities of annual accounting tasks and tax filing with
                    ease.
                  </p>
                </div>
                <div className="flex justify-center lg:justify-start">
                  <ButtonNew
                    as="a"
                    href="/sg/accounting-and-auditing"
                    othersClass={"capitalize"}
                  >
                    See our pricing plans
                  </ButtonNew>
                </div>
              </div>
              <div className="col-12 grid place-items-center lg:col-6">
                <Img
                  src="/assets/images/sg/company-incorporation-local/accounting-sg-incorp_local.png"
                  alt="accounting & tax filling"
                />
              </div>
            </div>
          </div>
        </section>
        <section className=" mb-0 lg:bg-bbc-brand-100 ">
          <div className="container">
            <div className="row items-center py-10 lg:py-20">
              <div className="justify-self-start lg:col-6">
                <div className="h-full">
                  <Title
                    text="Bring Your Next<br/> Business Venture Online"
                    className="bbc-title mb-6 text-center text-bbc-30px text-bbc-brand-800 lg:mb-0 lg:text-left lg:text-bbc-40px"
                  />
                </div>
              </div>
              <div className="lg:col-6">
                <div className="h-full">
                  <LazyLoad>
                    <iframe
                      src="https://www.youtube.com/embed/8RkJwKEXKPU?start=2"
                      width="540"
                      height="303"
                      frameBorder="0"
                      title="Bring your next business venture online"
                      allow="accelerometer; autoplay;encrypted-media; gyroscope;picture-in-picture"
                      allowFullScreen
                      className=" w-full"
                    ></iframe>
                  </LazyLoad>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section>
        <WhatMakeUsDifferent
          data={dataJson.dataWhatMakeUsDifferent}
          className="bg-wmud py-[60px]"
        />
      </section>

      <section>
        {dataJson.dataOpenBankAccount &&
          typeof dataJson.dataOpenBankAccount === "object" &&
          Object.keys(dataJson.dataOpenBankAccount).length > 0 && (
            <StepSlider
              data={dataJson.dataOpenBankAccount}
              imgAlt={dataJson.dataOpenBankAccount.alt}
              className="!pt-0"
            />
          )}
      </section>

      <section>
        <RequiredDocument
          title={dataJson.dataRequiredDocument.title || "Required Documents"}
          data={dataJson.dataRequiredDocument}
          sectionClassName="py-10 lg:py-20"
          backgroundImage="/assets/images/common/required-documents.png"
        />
      </section>

      <section className="container">
        {dataJson.dataEbookDownload && (
          <EbookDownload
            title={dataJson.dataEbookDownload.title}
            imgSrc={dataJson.dataEbookDownload.imgSrc}
            imgLeftSrc={dataJson.dataEbookDownload.imgLeftSrc}
            triggerName={dataJson.dataEbookDownload.triggerName}
            apiLink={dataJson.dataEbookDownload.apiLink}
            contactFromId={dataJson.dataEbookDownload.contactFromId}
            hash={dataJson.dataEbookDownload.hash}
            dataForm={dataJson.dataEbookDownload.dataForm}
            wrapper={{ className: "mb-10 lg:mb-20" }}
            alt={dataJson.dataEbookDownload.alt}
          >
            <p className="mb-6">
              Chart out a clear roadmap towards company formation with our
              Singapore Incorporation checklist. Start your journey with the
              following insights on
            </p>
            <ul>
              {[
                "Company pre-incorporation requirements",
                "Compact guideline of application procedures",
                "Checklist of required business licenses",
                "Post-incorporation and annual compliance keynotes",
              ].map((item) => (
                <li key={item.toString()} className="mb-2 last:mb-0">
                  <CheckContent data={item} />
                </li>
              ))}
            </ul>
          </EbookDownload>
        )}
      </section>

      <section>
        <HowItWorks
          title={"How we register your company "}
          subTitle="in 4 easy steps"
          data={dataJson.dataHowItWorks}
          className=""
        />
      </section>

      <section>
        {dataJson.dataCompanyNameCheck &&
          typeof dataJson.dataCompanyNameCheck === "object" &&
          Object.keys(dataJson.dataCompanyNameCheck).length > 0 && (
            <div className="pb-10 lg:pb-20">
              <CompanyNameCheck data={dataJson.dataCompanyNameCheck} />
            </div>
          )}
      </section>

      <section className="relative ">
        <div className=" absolute hidden h-1/2 w-full md:block lg:bg-bbc-brand-100"></div>
        <div className="container relative z-10">
          <div className="m-auto lg:col-10">
            <FormCountries
              title={dataJson.dataFormContact.title}
              description={dataJson.dataFormContact.description}
              country_id={191}
            />
          </div>
        </div>
        <Img
          src="/assets/images/wave/opposition_bottom-wave.png"
          width={1920}
          height={86}
          className="hidden  w-full lg:block"
          wrapper={{ className: "absolute w-full top-1/2 hidden md:block" }}
          alt="Wave bottom"
        />
      </section>

      <section>
        <div className="container">
          <div className="lg:col-10 lg:mx-auto">
            <Faqs
              data={dataJson.dataFaqs}
              title={dataJson.dataFaqs.title}
              description={dataJson.dataFaqs.description}
              defaultActiveKey={dataJson.dataFaqs.defaultActiveKey}
              multipleOpen={dataJson.dataFaqs.multipleOpen}
            />
          </div>
        </div>
      </section>

      <section className="mb-10 lg:mb-20">
        <div className="container">
          <Title className="title-h2 m-auto mb-10 w-2/3 pb-0 text-center text-bbc-brand-800">
            Industry News & Insights
          </Title>
          <LazyLoad>
            <NewsSlider data={data} />
          </LazyLoad>
        </div>
      </section>
      <FormNewsLetter
        bgColor="bg-bbc-gradient-14"
        descriptionBellow={
          <div className="mt-4 mb-3 text-left text-white">
            #start-ups, #pre & post incorporation, #corporate bank account, #tax
            advantages, #accounting standards, #audit exemption, #work permit
          </div>
        }
      />
    </>
  );
};

export default CompanyIncorporationLocal;
